// src/context/CartContext.jsx
import { createContext, useContext, useState, useEffect } from "react";
import { api } from "../utils/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  // Fetch cart when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setCart(null);
    }
  }, [isAuthenticated]);

  const fetchCart = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCart();
      if (res.success) {
        setCart(res.data.cart);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError("Failed to fetch cart");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    setError(null);
    const res = await api.addToCart({ productId, quantity });

    if (res.success) {
      setCart(res.data.cart);
      return { success: true, message: "Item added to cart" };
    }

    setError(res.message);
    return { success: false, message: res.message };
  };

  const updateCartItem = async (productId, quantity) => {
    setError(null);
    const res = await api.updateCart({ productId, quantity });

    if (res.success) {
      setCart(res.data.cart);
      return { success: true };
    }

    setError(res.message);
    return { success: false, message: res.message };
  };

  const removeFromCart = async (productId) => {
    setError(null);
    const res = await api.removeFromCart(productId);

    if (res.success) {
      setCart(res.data.cart);
      return { success: true, message: "Item removed from cart" };
    }

    setError(res.message);
    return { success: false, message: res.message };
  };

  const clearCart = async () => {
    setError(null);
    const res = await api.clearCart();

    if (res.success) {
      setCart(res.data.cart);
      return { success: true, message: "Cart cleared" };
    }

    setError(res.message);
    return { success: false, message: res.message };
  };

  const getCartTotal = () => {
    return cart?.totalAmount || 0;
  };

  const getCartItemsCount = () => {
    return cart?.totalItems || 0;
  };

  const value = {
    cart,
    loading,
    error,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    fetchCart,
    getCartTotal,
    getCartItemsCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};